import Button from '@atlaskit/button/new';
import Heading from '@atlaskit/heading';
import Lozenge from '@atlaskit/lozenge';
import { Box, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import { graphql, useFragment } from 'react-relay';

import { useCreateFlowSession } from '../../hooks/use-create-flow';

import type { card_fragment$key } from './__generated__/card_fragment.graphql';
import { useRepositoryModal } from './repository-modal';

type CardProps = {
	issue: card_fragment$key;
};

export const Card = ({ issue }: CardProps) => {
	const data = useFragment(
		graphql`
			fragment card_fragment on JiraIssue {
				id
				key
				summary
				fieldsById(ids: ["issuetype", "status", "description"]) {
					edges {
						node {
							fieldId
							... on JiraIssueTypeField {
								issueType {
									name
									avatar {
										small
									}
								}
							}
							... on JiraStatusField {
								id
								status {
									name
								}
							}
							... on JiraRichTextField {
								richText {
									adfValue {
										convertedPlainText {
											plainText
										}
									}
								}
							}
						}
					}
				}
			}
		`,
		issue,
	);

	const { id, key, summary, fieldsById } = data;

	const getFieldByFieldId = (fieldId: string) =>
		fieldsById?.edges?.find((edge) => edge?.node?.fieldId === fieldId);

	const issueType = getFieldByFieldId('issuetype')?.node?.issueType;
	const description =
		getFieldByFieldId('description')?.node?.richText?.adfValue?.convertedPlainText?.plainText;

	const [createFlow, isPending] = useCreateFlowSession();

	const issueForSession = {
		icon: issueType?.avatar?.small ?? '',
		key,
		summary: summary ?? '',
		description: description ?? '',
	};

	const { openModal, RepositoryModal } = useRepositoryModal({
		branchName: `${key}-feature`,
		onConfirm: (repositoryUrl, branchName, prompt) => {
			createFlow({
				issueARI: id,
				repoUrl: repositoryUrl,
				issue: issueForSession,
				branchName,
				prompt,
			});
		},
		isSubmitPending: isPending,
		selectedIssue: issueForSession,
	});

	return (
		<Stack xcss={containerStyles} spread="space-between">
			<Text as="span" weight="bold" maxLines={1}>
				{summary}
			</Text>
			<Text as="span" maxLines={1}>
				{description}
			</Text>
			<Box as="span">
				<Lozenge appearance="new">{getFieldByFieldId('status')?.node?.status?.name}</Lozenge>
			</Box>
			<Inline spread="space-between" alignBlock="center">
				<Inline space="space.100">
					<img
						alt={issueType?.name ?? ''}
						src={issueType?.avatar?.small ?? ''}
						width={16}
						height={16}
					/>

					<Heading size="xxsmall">{key}</Heading>
				</Inline>
				<Box>
					<Button onClick={openModal}>Start flow</Button>
				</Box>
			</Inline>
			{RepositoryModal}
		</Stack>
	);
};

const containerStyles = xcss({
	padding: 'space.150',
	minWidth: '260px',
	minHeight: '148px',
	backgroundColor: 'elevation.surface.raised',
	borderRadius: 'border.radius.100',
	boxShadow: 'elevation.shadow.raised',
	':hover': {
		backgroundColor: 'elevation.surface.hovered',
	},
});
